// utilities/SessionManager.js - OPTIMIZED VERSION - Reduced console noise and improved performance
class SessionManager {
    constructor() {
        this.STORAGE_KEY_PREFIX = 'auth_';
        this.SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
        this.WARNING_TIME = 5 * 60 * 1000; // 5 minutes before expiry
        this.inactivityTimer = null;
        this.warningTimer = null;
        this.onSessionExpired = null;
        this.onSessionWarning = null;

        // ADDED: Caching to reduce frequent validation calls
        this.lastValidationTime = 0;
        this.lastValidationResult = false;
        this.validationCacheTimeout = 5000; // Cache validation result for 5 seconds

        // ADDED: Flag to track if we're in a context where session validation is expected
        this.expectSession = false;

        // FIX: Track last time we wrote the refreshed expiresAt back to storage.
        // resetInactivityTimer() is called on every mouse-move / click, so we
        // throttle storage writes to once per minute to avoid excessive I/O while
        // still keeping the stored expiresAt in sync with real user activity.
        this.lastStorageRefreshTime = 0;
        this.storageRefreshThrottle = 60 * 1000; // 60 seconds

        this.initializeSessionTracking();
    }

    // Initialize session tracking and browser close detection
    initializeSessionTracking() {
        // NOTE: beforeunload/pagehide are intentionally NOT used here.
        // They fire on both tab close AND page refresh, which would silently
        // log the user out on every F5/refresh. Session expiry is handled
        // exclusively by the 30-minute inactivity timer instead.

        // Track user activity
        const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
        activityEvents.forEach(event => {
            document.addEventListener(event, () => this.resetInactivityTimer(), true);
        });

        // REMOVED: Check for existing session on page load - this was causing early validation
        // this.validateSession();
    }

    // Set session data with expiration
    setSession(key, value, useSessionStorage = true) {
        const storage = useSessionStorage ? sessionStorage : localStorage;
        const sessionData = {
            value: value,
            timestamp: Date.now(),
            expiresAt: Date.now() + this.SESSION_TIMEOUT
        };

        storage.setItem(this.STORAGE_KEY_PREFIX + key, JSON.stringify(sessionData));
        this.resetInactivityTimer();

        // IMPROVED: Only log in development or when explicitly needed
        if (process.env.NODE_ENV === 'development') {
        }

        // ADDED: Clear validation cache when new session is set
        this.lastValidationTime = 0;
        this.expectSession = true;
    }

    // Get session data and check expiration
    getSession(key, useSessionStorage = true) {
        const storage = useSessionStorage ? sessionStorage : localStorage;
        const storedData = storage.getItem(this.STORAGE_KEY_PREFIX + key);

        if (!storedData) {
            return null;
        }

        try {
            const sessionData = JSON.parse(storedData);

            // Check if session has expired
            if (Date.now() > sessionData.expiresAt) {
                // IMPROVED: Only log expiration if we expected a session
                if (this.expectSession) {
                }
                this.removeSession(key, useSessionStorage);
                return null;
            }

            return sessionData.value;
        } catch (error) {
            this.removeSession(key, useSessionStorage);
            return null;
        }
    }

    // Remove specific session data
    removeSession(key, useSessionStorage = true) {
        const storage = useSessionStorage ? sessionStorage : localStorage;
        storage.removeItem(this.STORAGE_KEY_PREFIX + key);
    }

    // Clear all session data
    clearSession() {
        // IMPROVED: Only log when there's actually something to clear
        const hasSessionData = this.hasAnySessionData();
        
        if (hasSessionData) {
        }

        // Clear sessionStorage
        Object.keys(sessionStorage).forEach(key => {
            if (key.startsWith(this.STORAGE_KEY_PREFIX)) {
                sessionStorage.removeItem(key);
            }
        });

        // Clear localStorage auth data
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith(this.STORAGE_KEY_PREFIX) ||
                ['employeeId', 'employeeData', 'userData', 'roleData', 'roleId'].includes(key)) {
                localStorage.removeItem(key);
            }
        });

        this.clearInactivityTimer();

        // ADDED: Reset validation cache and expectation
        this.lastValidationTime = 0;
        this.lastValidationResult = false;
        this.expectSession = false;

        // FIX: Reset throttle counter so the first activity after next login
        // always writes a fresh expiresAt immediately.
        this.lastStorageRefreshTime = 0;
    }

    // ADDED: Helper method to check if any session data exists
    hasAnySessionData() {
        // Quick check for any session-related data
        const hasSessionStorage = Object.keys(sessionStorage).some(key => 
            key.startsWith(this.STORAGE_KEY_PREFIX)
        );
        
        const hasLocalStorage = Object.keys(localStorage).some(key => 
            key.startsWith(this.STORAGE_KEY_PREFIX) || 
            ['employeeId', 'employeeData', 'userData', 'roleData', 'roleId'].includes(key)
        );

        return hasSessionStorage || hasLocalStorage;
    }

    // IMPROVED: Validate current session with caching
    validateSession() {
        // ADDED: Use cached result if recent validation was done
        const now = Date.now();
        if (now - this.lastValidationTime < this.validationCacheTimeout) {
            return this.lastValidationResult;
        }

        const employeeId = this.getSession('employeeId');
        const userData = this.getSession('userData');

        // Fallback: if sessionStorage was cleared (e.g. after a page refresh that
        // didn't go through beforeunload), treat localStorage auth data as still valid
        // and re-hydrate sessionStorage so subsequent checks pass.
        if (!employeeId && !userData) {
            const lsEmployeeId = localStorage.getItem('employeeId');
            const lsUserData   = localStorage.getItem('userData');
            if (lsEmployeeId || lsUserData) {
                if (lsEmployeeId) this.setSession('employeeId', lsEmployeeId);
                if (lsUserData)   this.setSession('userData', JSON.parse(lsUserData));
                this.lastValidationResult = true;
                this.lastValidationTime = now;
                this.expectSession = true;
                return true;
            }

            if (this.expectSession) {
            }
            this.lastValidationResult = false;
            this.lastValidationTime = now;
            return false;
        }

        // Check if any session data has expired
        const sessionKeys = ['employeeId', 'userData', 'roleData', 'roleId', 'employeeData'];
        let hasValidSession = false;

        sessionKeys.forEach(key => {
            const data = this.getSession(key);
            if (data) {
                hasValidSession = true;
            }
        });

        if (hasValidSession) {
            // IMPROVED: Only log significant validations
            if (!this.lastValidationResult || this.expectSession) {
            }
            this.resetInactivityTimer();
            this.lastValidationResult = true;
            this.expectSession = true;
        } else {
            // IMPROVED: Only log if we expected a session
            if (this.expectSession) {
            }
            this.clearSession();
            this.lastValidationResult = false;
        }

        this.lastValidationTime = now;
        return this.lastValidationResult;
    }

    // Reset inactivity timer
    resetInactivityTimer() {
        this.clearInactivityTimer();

        // FIX: Keep storage-based expiresAt in sync with real user activity.
        //
        // Previously, expiresAt was only written on login or an explicit
        // extendSession() call.  The JavaScript timers (below) were correctly
        // reset on every activity event, but getSessionStatus() / getSession()
        // read expiresAt from sessionStorage — so after 30 min from login the
        // storage check would declare the session expired and log the user out
        // even though they had been working continuously in the inbox.
        //
        // We throttle the storage write to once per minute so that frequent
        // events like mousemove don't cause excessive sessionStorage I/O.
        const now = Date.now();
        if (this.expectSession && (now - this.lastStorageRefreshTime >= this.storageRefreshThrottle)) {
            this.lastStorageRefreshTime = now;
            const newExpiresAt = now + this.SESSION_TIMEOUT;
            const keysToRefresh = ['employeeId', 'userData', 'roleData', 'roleId', 'employeeData'];
            keysToRefresh.forEach(key => {
                const storageKey = this.STORAGE_KEY_PREFIX + key;
                const raw = sessionStorage.getItem(storageKey);
                if (raw) {
                    try {
                        const parsed = JSON.parse(raw);
                        parsed.expiresAt = newExpiresAt;
                        sessionStorage.setItem(storageKey, JSON.stringify(parsed));
                    } catch (e) {
                        // Ignore – malformed entry will be caught on next getSession() call
                    }
                }
            });
        }

        // Set warning timer (5 minutes before expiry)
        this.warningTimer = setTimeout(() => {
            if (this.onSessionWarning) {
                this.onSessionWarning();
            }
        }, this.SESSION_TIMEOUT - this.WARNING_TIME);

        // Set expiry timer
        this.inactivityTimer = setTimeout(() => {
            this.handleSessionExpiry();
        }, this.SESSION_TIMEOUT);
    }

    // Clear inactivity timers
    clearInactivityTimer() {
        if (this.inactivityTimer) {
            clearTimeout(this.inactivityTimer);
            this.inactivityTimer = null;
        }
        if (this.warningTimer) {
            clearTimeout(this.warningTimer);
            this.warningTimer = null;
        }
    }

    // Handle session expiry
    handleSessionExpiry() {
        this.clearSession();

        if (this.onSessionExpired) {
            this.onSessionExpired();
        } else {
            // Default behavior - redirect to login
            window.location.href = '/login';
        }
    }

    // Set callback for session expiry
    onExpired(callback) {
        this.onSessionExpired = callback;
    }

    // Set callback for session warning
    onWarning(callback) {
        this.onSessionWarning = callback;
    }

    // Extend session (refresh expiry time)
    extendSession() {
        const sessionKeys = ['employeeId', 'userData', 'roleData', 'roleId', 'employeeData'];

        sessionKeys.forEach(key => {
            const data = this.getSession(key);
            if (data) {
                this.setSession(key, data);
            }
        });

        
        // ADDED: Clear validation cache after extension
        this.lastValidationTime = 0;
    }

    // Get session status
    getSessionStatus() {
        const employeeId = this.getSession('employeeId');
        const userData = this.getSession('userData');

        if (!employeeId && !userData) {
            return {
                isValid: false,
                remainingTime: 0,
                message: 'No active session'
            };
        }

        // Calculate remaining time based on the latest session data
        const sessionData = sessionStorage.getItem(this.STORAGE_KEY_PREFIX + 'employeeId') ||
            sessionStorage.getItem(this.STORAGE_KEY_PREFIX + 'userData');

        if (sessionData) {
            try {
                const parsed = JSON.parse(sessionData);
                const remainingTime = parsed.expiresAt - Date.now();

                return {
                    isValid: remainingTime > 0,
                    remainingTime: Math.max(0, remainingTime),
                    message: remainingTime > 0 ? 'Session active' : 'Session expired'
                };
            } catch (error) {
                return {
                    isValid: false,
                    remainingTime: 0,
                    message: 'Invalid session data'
                };
            }
        }

        return {
            isValid: false,
            remainingTime: 0,
            message: 'No session data found'
        };
    }

    // IMPROVED: Check if user is authenticated with better performance
    isAuthenticated() {
        try {
            // IMPROVED: Quick check first - if no basic session data, return false quietly
            const hasAnySession = localStorage.getItem('employeeId') ||
                sessionStorage.getItem('auth_employeeId') ||
                sessionStorage.getItem('auth_userData');

            if (!hasAnySession) {
                return false; // Don't log - this is normal for login pages
            }

            // ADDED: Use cached validation if available and recent, or if forced valid after login
            const now = Date.now();
            if ((now - this.lastValidationTime < this.validationCacheTimeout) || this.lastValidationResult === true) {
                return this.lastValidationResult;
            }

            // Only do full validation if we have session data
            this.expectSession = true;
            return this.validateSession();
        } catch (error) {
            // IMPROVED: Only log actual errors, not normal cases
            return false;
        }
    }

    // Login method - save authentication data
    login(authData) {

        if (authData.employeeId) {
            this.setSession('employeeId', authData.employeeId);
        }

        if (authData.userData) {
            this.setSession('userData', authData.userData);
        }

        if (authData.roleData) {
            this.setSession('roleData', authData.roleData);
        }

        if (authData.roleId) {
            this.setSession('roleId', authData.roleId);
        }

        if (authData.employeeData) {
            this.setSession('employeeData', authData.employeeData);
        }

        this.resetInactivityTimer();
        
        // IMPROVED: Force immediate validation update after login
        this.expectSession = true;
        this.lastValidationTime = 0;
        this.lastValidationResult = true; // Force immediate valid state
        
    }

    // Logout method
    logout() {
        this.clearSession();
    }
}

// Create singleton instance
const sessionManager = new SessionManager();

export default sessionManager;